import { apiDataSource, shapeEntity } from 'utils/entities';
import { fetchEntities } from 'utils/api/entities.api';
import memoize from 'memoize-async';
import objectHash from 'object-hash';

/** The ancestor chain whose names are the ones `getHierarchy` settled on. */
const chosenPath = (category, getHierarchy) => {
    const paths = category.paths || [];
    if (paths.length < 2) return paths[0] || [];
    const { hierarchy_hint: hint } = getHierarchy(category.hierarchies);
    if (!hint) return paths[0];
    return (
        paths.find((path) =>
            path.every(({ depth, name }) => hint[depth] === name),
        ) || paths[0]
    );
};

/**
 * A category's trail up to its root, in the taxonomy of the source being viewed.
 *
 * Two requests, not one per level: a hierarchy row already spells out the whole
 * ancestry, which the resolver returns as `paths`. That also keeps one
 * plan-aware path for the whole trail, which matters for the categories that
 * sit under more than one parent.
 */
export const getBreadCrumbs = memoize(
    async (data, dataSource, getHierarchy) => {
        if (!data || !(data.id || data.name)) return [];

        const source = apiDataSource(dataSource);
        const entities = await fetchEntities({
            dataSource: source,
            entityType: 'categories',
            includePaths: true,
            ...(data.name ? { names: [data.name] } : { ids: [data.id] }),
        });

        // Looking up by name is how an industry root is resolved, and a name is
        // not unique across depths -- only a cat1 is an industry.
        const candidates = Object.values(entities || {})
            .map(shapeEntity)
            .filter(({ depth }) => !data.name || depth === 'cat1');

        if (candidates.length < 1) return [];

        const category = candidates[0];
        const ancestorIds = chosenPath(category, getHierarchy)
            .slice(0, -1)
            .map(({ id }) => id);

        // Crumbs are whole entities, not just {id, name}: the menus read each
        // one's `children` and `hierarchies`.
        const ancestors = ancestorIds.length
            ? await fetchEntities({
                  dataSource: source,
                  entityType: 'categories',
                  ids: ancestorIds,
              })
            : {};

        return [
            { name: 'Industries' },
            ...ancestorIds
                .map((id) => ancestors?.[id])
                .filter(Boolean)
                .map(shapeEntity),
            category,
        ];
    },
    (...args) => objectHash([...args]),
);
